import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchUserData } from '../../../utils/api';
import { formatearFecha } from '../../../utils/fecha';
import axiosInstance from '../../../services/AxiosInstance';
import BotonChico03 from '../../../components/Botones/BotonChico03/BotonChico03';
import { ROUTES } from '../../../const/routes';
import { getAvatarSrc, handleAvatarError } from '../../../utils/avatar';
import { useTranslation } from 'react-i18next';

const JUGADAS_POR_PAGINA = 10;

const DetalleJuegoHis = ({ userId }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);

  const [jugadorNick, setJugadorNick] = useState('');
  const [jugadorAvatar, setJugadorAvatar] = useState('');

  const [jugadas, setJugadas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [filtro, setFiltro] = useState('All');
  const [ordenar, setOrdenar] = useState('recientes');
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  const fetchJugadas = useCallback(async () => {
    setCargando(true);
    try {
      const url = userId
        ? `/api/historial/jugadas/${userId}`
        : '/api/historial/jugadas';

      const response = await axiosInstance.get(url, {
        params: {
          page: currentPage,
          limit: JUGADAS_POR_PAGINA,
          filtro,
          ...(ordenar !== 'recientes' && { ordenar }),
        },
      });

      const { data, totalPages, nick_usuario, avatar } = response.data;

      if (userId && nick_usuario) {
        setJugadorNick(nick_usuario);
        setJugadorAvatar(avatar || '');
      }

      const jugadasFormateadas = data.map((jugada) => ({
        ...jugada,
        hora_jugada_local: formatearFecha(jugada.hora_jugada),
      }));

      setJugadas(jugadasFormateadas);
      setTotalPaginas(totalPages || 1);
    } catch (error) {
      console.error('Error al obtener las jugadas:', error);
      setJugadas([]);
      setTotalPaginas(1);
    } finally {
      setCargando(false);
    }
  }, [currentPage, filtro, ordenar, userId]);

  useEffect(() => {
    fetchJugadas();
  }, [fetchJugadas]);

  const handleFiltroChange = (e) => {
    setFiltro(e.target.value);
    setCurrentPage(1);
  };

  const handleOrdenarChange = (e) => {
    setOrdenar(e.target.value);
    setCurrentPage(1);
  };

  const getAccentColor = useCallback((tipo) => {
    switch (tipo) {
      case 'Roulette':
        return {
          border: 'border-l-green-500',
          badge: 'bg-green-500/20 text-green-400',
        };
      case 'Slots':
        return {
          border: 'border-l-yellow-500',
          badge: 'bg-yellow-500/20 text-yellow-400',
        };
      case 'BlackJack':
        return {
          border: 'border-l-blue-500',
          badge: 'bg-blue-500/20 text-blue-400',
        };
      default:
        return {
          border: 'border-l-gray-500',
          badge: 'bg-gray-500/20 text-gray-400',
        };
    }
  }, []);

  const avatarSrc = getAvatarSrc(jugadorAvatar);

  const navigateState = userId
    ? { from: 'historialJugador', userId }
    : undefined;

  return (
    <div className="flex flex-col items-center py-6 md:py-10 px-2 lg:px-10">
      {userId && jugadorNick ? (
        <>
          <div className="flex items-center gap-3 mb-8">
            <img
              src={avatarSrc}
              alt={`Avatar de ${jugadorNick}`}
              onError={handleAvatarError}
              className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-slate-500 bg-gray-600 object-cover"
            />
            <h2 className="text-lg md:text-xl lg:text-2xl text-yellow-500 font-bold">
              {t('historyDetail.otherPlayerTitle', { playerNick: jugadorNick })}
            </h2>
          </div>
          <p className="text-[10px] md:text-xs lg:text-sm text-gray-300 mb-4 md:mb-8 text-center">
            {t('historyDetail.otherPlayerSubtitle')}
          </p>
        </>
      ) : (
        <>
          <h2 className="text-lg md:text-xl lg:text-2xl text-yellow-500 font-bold mb-3 md:mb-4">
            {t('historyDetail.myTitle')}
          </h2>
          <p className="text-[10px] md:text-xs lg:text-sm text-gray-300 mb-4 md:mb-8 text-center">
            {t('historyDetail.mySubtitle')}
          </p>
        </>
      )}
      <div className="mb-4 md:mb-6 flex flex-wrap justify-center items-center gap-3 md:gap-5">
        <div className="flex items-center">
          <label className="text-gray-300 mr-2 text-xs md:text-sm font-medium">
            {t('historyDetail.filterLabel')}
          </label>
          <select
            value={filtro}
            onChange={handleFiltroChange}
            className="appearance-none bg-gray-700/80 text-white py-2 px-4 pr-10 rounded-lg text-xs md:text-sm border border-gray-600 focus:border-white focus:outline-none transition-colors cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22white%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat"
          >
            <option value="All">{t('historyDetail.filterAll')}</option>
            <option value="Roulette">{t('historyDetail.filterRoulette')}</option>
            <option value="Slots">{t('historyDetail.filterSlots')}</option>
            <option value="BlackJack">{t('historyDetail.filterBlackjack')}</option>
          </select>
        </div>
        <div className="flex items-center">
          <label className="text-gray-300 mr-2 text-xs md:text-sm font-medium">
            {t('historyDetail.sortLabel')}
          </label>
          <select
            value={ordenar}
            onChange={handleOrdenarChange}
            className="appearance-none bg-gray-700/80 text-white py-2 px-4 pr-10 rounded-lg text-xs md:text-sm border border-gray-600 focus:border-white focus:outline-none transition-colors cursor-pointer bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2020%2020%22%20fill%3D%22white%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20d%3D%22M5.23%207.21a.75.75%200%20011.06.02L10%2011.168l3.71-3.938a.75.75%200%20111.08%201.04l-4.25%204.5a.75.75%200%2001-1.08%200l-4.25-4.5a.75.75%200%2001.02-1.06z%22%20clip-rule%3D%22evenodd%22%2F%3E%3C%2Fsvg%3E')] bg-[length:1.25rem_1.25rem] bg-[right_0.5rem_center] bg-no-repeat"
          >
            <option value="recientes">{t('historyDetail.sortNewest')}</option>
            <option value="mayor_premio">{t('historyDetail.sortHighest')}</option>
            <option value="menor_premio">{t('historyDetail.sortLowest')}</option>
          </select>
        </div>
      </div>

      {/* Contenedor de tabla */}
      <div className="w-full">
        <div
          className={`bg-bgContenedorOscuro rounded-lg px-2 md:px-5
                      border-solid border-4 border-borderContenedorOscuro shadow-shadowContenedorOscuro
                      shadow-[0_0_20px_rgba(100,116,139,0.9)] transition-all duration-300 ease-in-out hover:shadow-[0_0_30px_rgba(100,116,139,1)]`}
        >
          {/* Vista Desktop - Tabla */}
          <div className="hidden md:block">
            <div className="w-full grid grid-cols-5 text-yellow-500 py-4 px-4 text-xs font-semibold uppercase tracking-wider items-center border-b border-gray-700/50">
              <div className="text-center">{t('historyDetail.columnBet')}</div>
              <div className="text-center">{t('historyDetail.columnWin')}</div>
              <div className="text-center">{t('historyDetail.columnGame')}</div>
              <div className="text-center">{t('historyDetail.columnDate')}</div>
              <div className="text-center">{t('historyDetail.columnDetails')}</div>
            </div>
            <div className="w-full">
              {cargando ? (
                <div className="text-center text-gray-300 py-10 animate-pulse">
                  {t('historyDetail.loading')}
                </div>
              ) : jugadas.length === 0 ? (
                <div className="text-center text-gray-300 py-10">
                  {t('historyDetail.noRecords')}
                </div>
              ) : (
                jugadas.map((jugada, index) => {
                  const accent = getAccentColor(jugada.nombre_juego);
                  return (
                    <div
                      key={jugada.id}
                      className={`grid grid-cols-5 py-3 px-4 text-center text-gray-200 text-xs border-l-4 ${accent.border} border-b border-gray-700/30 transition-colors duration-150 hover:bg-white/5 ${
                        index % 2 === 0 ? 'bg-white/[0.03]' : 'bg-transparent'
                      }`}
                    >
                      <div className="py-1 flex justify-center items-center">
                        ${jugada.total_apostado}
                      </div>
                      <div className="py-1 flex justify-center items-center">
                        ${jugada.total_recompensa}
                      </div>
                      <div className="py-1 flex justify-center items-center gap-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${accent.badge}`}
                        >
                          {t(`backend.gameName.${jugada.nombre_juego}`, jugada.nombre_juego)}
                        </span>
                        {jugada.en_proceso && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-400">
                            {t('historyDetail.inProgress')}
                          </span>
                        )}
                      </div>
                      <div className="py-1 flex justify-center items-center text-gray-400">
                        {jugada.hora_jugada_local}
                      </div>
                      <div className="py-1 flex justify-center items-center">
                        <BotonChico03
                          texto={t('historyDetail.detailsButton')}
                          funcion={() =>
                            navigate(
                              `${ROUTES.detallesJugada}/${jugada.id}`,
                              navigateState
                                ? { state: navigateState }
                                : undefined
                            )
                          }
                          bgColor="blue"
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Vista Móvil - Tarjetas */}
          <div className="md:hidden py-4 space-y-3">
            {cargando ? (
              <div className="text-center text-gray-300 py-10 animate-pulse">
                {t('historyDetail.loading')}
              </div>
            ) : jugadas.length === 0 ? (
              <div className="text-center text-gray-300 py-10">
                {t('historyDetail.noRecords')}
              </div>
            ) : (
              jugadas.map((jugada) => {
                const accent = getAccentColor(jugada.nombre_juego);
                return (
                  <div
                    key={jugada.id}
                    className={`bg-gray-800/60 text-gray-200 rounded-lg p-3 text-[10px] sm:text-xs border-l-4 ${accent.border} border border-gray-700/30`}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center gap-1">
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${accent.badge}`}
                        >
                          {t(`backend.gameName.${jugada.nombre_juego}`, jugada.nombre_juego)}
                        </span>
                        {jugada.en_proceso && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-orange-500/20 text-orange-400">
                            {t('historyDetail.inProgress')}
                          </span>
                        )}
                      </div>
                      <span className="text-gray-500 text-[9px] sm:text-[10px]">
                        {jugada.hora_jugada_local}
                      </span>
                    </div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-400">{t('historyDetail.betLabel')}</span>
                      <span className="font-medium">
                        ${jugada.total_apostado}
                      </span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span className="text-gray-400">{t('historyDetail.winLabel')}</span>
                      <span className="font-medium">
                        ${jugada.total_recompensa}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      <BotonChico03
                        texto={t('historyDetail.detailsButton')}
                        funcion={() =>
                          navigate(
                            `${ROUTES.detallesJugada}/${jugada.id}`,
                            navigateState ? { state: navigateState } : undefined
                          )
                        }
                        bgColor="blue"
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Paginación */}
          <div className="flex flex-wrap justify-center items-center my-6 md:my-10 gap-2 md:gap-4">
            <BotonChico03
              texto={t('historyDetail.previousButton')}
              bgColor={currentPage === 1 ? 'gray' : 'pink'}
              funcion={() => {
                if (currentPage > 1) {
                  setCurrentPage((prev) => prev - 1);
                }
              }}
            />
            <span className="text-gray-300 py-1 px-3 whitespace-nowrap text-xs md:text-base">
              {currentPage} / {totalPaginas}
            </span>
            <BotonChico03
              texto={t('historyDetail.nextButton')}
              bgColor={currentPage === totalPaginas ? 'gray' : 'blue'}
              funcion={() => {
                if (currentPage < totalPaginas) {
                  setCurrentPage((prev) => prev + 1);
                }
              }}
            />
          </div>
        </div>
      </div>

      {userId && (
        <div className="mt-6">
          <BotonChico03
            texto={t('historyDetail.backToRanking')}
            bgColor="pink"
            funcion={() => navigate(ROUTES.ranking)}
          />
        </div>
      )}
    </div>
  );
};

export default DetalleJuegoHis;
